import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FamilyIcon } from '../icons/SvgIcons';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  Divider,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert as MuiAlert,
} from '@mui/material';
import {
  LiveTv,
  VideoLibrary,
  ExitToApp,
  VideoCall,
  NotificationsActive,
  Add,
  Delete,
  Warning,
  Info,
  PersonSearch,
  History,
  SummarizeOutlined,
  SmartToy,
  CalendarMonth,
  Close,
  PlayArrow,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { LiveCallsList } from '../calls/LiveCallsList';
import { FamilyPatientInfo } from './FamilyPatientInfo';
import { apiRequest } from '../../utils/api';

export const FamilyDashboard = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const { user, logout } = useAuthStore();

  // Reminders
  const [reminders, setReminders] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderForm, setReminderForm] = useState({ content: '', remind_at: '', recurrence: '' });

  // Alerts
  const [alerts, setAlerts] = useState([]);

  // Family relationships (to know patient IDs)
  const [linkedPatients, setLinkedPatients] = useState([]);

  useEffect(() => {
    fetchAlerts();
    fetchLinkedPatients();
  }, []);

  const fetchAlerts = async () => {
    try {
      const data = await apiRequest('/alerts');
      setAlerts(data.alerts || []);
    } catch (e) { setAlerts([]); }
  };

  const fetchLinkedPatients = async () => {
    try {
      const data = await apiRequest('/patients/my-patients');
      const patients = data.patients || [];
      setLinkedPatients(patients);
    } catch (e) { setLinkedPatients([]); }
  };

  const fetchReminders = async (patientId) => {
    try {
      const data = await apiRequest(`/reminders/?patient_id=${patientId}`);
      setReminders(data.reminders || []);
    } catch (e) { setReminders([]); }
  };

  const createReminder = async () => {
    if (!linkedPatients.length) return;
    try {
      await apiRequest('/reminders/', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: linkedPatients[0].id,
          content: reminderForm.content,
          remind_at: new Date(reminderForm.remind_at).toISOString(),
          recurrence: reminderForm.recurrence || undefined,
        }),
      });
      setShowReminderDialog(false);
      setReminderForm({ content: '', remind_at: '', recurrence: '' });
      if (linkedPatients.length) fetchReminders(linkedPatients[0].id);
    } catch (e) { console.error('Error creating reminder:', e); }
  };

  const deleteReminder = async (id) => {
    try {
      await apiRequest(`/reminders/${id}`, { method: 'DELETE' });
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (e) {}
  };

  const callPatient = async (patientId) => {
    try {
      const response = await apiRequest('/calls/family-direct', {
        method: 'POST',
        body: JSON.stringify({ target_user_id: patientId }),
      });
      navigate(`/video-call/${response.call_id}`, {
        state: {
          token: response.token,
          serverUrl: response.server_url,
          roomName: response.room_id,
          mode: 'family_direct',
        },
      });
    } catch (e) { console.error('Error starting family call:', e); }
  };

  useEffect(() => {
    if (linkedPatients.length) fetchReminders(linkedPatients[0].id);
  }, [linkedPatients]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Top Navigation */}
      <Box
        component="header"
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h5"
                onClick={() => navigate('/')}
                sx={{
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.03em',
                }}
              >
                Vincula
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.8rem' }}>
                  {(user?.first_name?.[0] || 'F').toUpperCase()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.2}>
                    {user?.first_name} {user?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" lineHeight={1}>
                    Familiar
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={logout}
                startIcon={<ExitToApp sx={{ fontSize: 18 }} />}
                sx={{ fontSize: '0.8rem', py: 0.75 }}
              >
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Panel familiar <FamilyIcon width={28} height={28} color="#7C3AED" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sigue en tiempo real las videollamadas, crea recordatorios y recibí alertas.
          </Typography>
        </Box>

        {/* Warnings */}
        {linkedPatients.length === 0 && (
          <MuiAlert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            No tenés ningún paciente vinculado. Contactá al administrador para que te asigne uno.
          </MuiAlert>
        )}

        {/* Main Content */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              sx={{ px: 3, minHeight: 48 }}
            >
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonSearch sx={{ fontSize: 18 }} />
                    <span>Info familiar</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LiveTv sx={{ fontSize: 18 }} />
                    <span>En vivo</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <History sx={{ fontSize: 18 }} />
                    <span>Historial</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <VideoLibrary sx={{ fontSize: 18 }} />
                    <span>Grabaciones</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <VideoCall sx={{ fontSize: 18 }} />
                    <span>Llamar</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <NotificationsActive sx={{ fontSize: 18 }} />
                    <span>Recordatorios</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Warning sx={{ fontSize: 18 }} />
                    <span>Alertas</span>
                    {alerts.filter(a => !a.notified).length > 0 && (
                      <Chip label={alerts.filter(a => !a.notified).length} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FAFBFC' }}>
            {/* Tab 0: Patient Info */}
            {currentTab === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>Información de tu familiar</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recuerdos, datos médicos y alertas detectadas por el asistente de IA.
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <FamilyPatientInfo />
              </motion.div>
            )}

            {currentTab === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>Videollamadas en curso</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Puedes unirte como observador sin interrumpir la sesión.
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <LiveCallsList userRole="family" showOnlyFamily={true} />
              </motion.div>
            )}

            {currentTab === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h4" sx={{ mb: 0.5 }}>Historial de llamadas</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resúmenes y detalles de las sesiones con acompañantes humanos.
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small" startIcon={<CalendarMonth />} onClick={() => navigate('/calendar')}>
                      Calendario
                    </Button>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <CallHistoryList linkedPatients={linkedPatients} />
              </motion.div>
            )}

            {currentTab === 3 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>Grabaciones de sesiones</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Grabaciones de las sesiones con acompañantes de Vincula.
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <RecordingsList />
              </motion.div>
            )}

            {/* Call Patient Tab */}
            {currentTab === 4 && (
              <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'center', py: 4 }}>
                <VideoCall sx={{ fontSize: 64, color: '#2563EB', mb: 2 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>Llamar a tu familiar</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Iniciá una videollamada directa con tu familiar.
                </Typography>
                {linkedPatients.length > 0 ? (
                  <Stack spacing={1.5}>
                    {linkedPatients.map(p => (
                      <Button
                        key={p.id}
                        variant="contained"
                        size="large"
                        startIcon={<VideoCall />}
                        onClick={() => callPatient(p.id)}
                        sx={{ borderRadius: 3 }}
                      >
                        Llamar a {p.first_name} {p.last_name}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay pacientes vinculados.
                  </Typography>
                )}
              </Box>
            )}

            {/* Reminders Tab */}
            {currentTab === 5 && (
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h4">Recordatorios para tu familiar</Typography>
                  <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setShowReminderDialog(true)}>
                    Crear
                  </Button>
                </Stack>
                {reminders.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
                    <NotificationsActive sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      Creá recordatorios para que tu familiar no se olvide de tomar sus medicamentos u otras actividades.
                    </Typography>
                  </Paper>
                ) : (
                  <List>
                    {reminders.map(r => (
                      <ListItem key={r.id}
                        secondaryAction={<IconButton onClick={() => deleteReminder(r.id)}><Delete fontSize="small" /></IconButton>}
                        sx={{ bgcolor: 'white', mb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                      >
                        <ListItemIcon>
                          <NotificationsActive color={r.status === 'pending' ? 'warning' : 'disabled'} />
                        </ListItemIcon>
                        <ListItemText primary={r.content} secondary={new Date(r.remind_at).toLocaleString('es-ES')} />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Dialog open={showReminderDialog} onClose={() => setShowReminderDialog(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Crear recordatorio</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <TextField
                        label="¿Qué hay que recordarle?"
                        value={reminderForm.content}
                        onChange={e => setReminderForm(prev => ({ ...prev, content: e.target.value }))}
                        fullWidth
                        placeholder="Ej: Tomar la pastilla de la presión"
                      />
                      <TextField
                        label="Fecha y hora"
                        type="datetime-local"
                        value={reminderForm.remind_at}
                        onChange={e => setReminderForm(prev => ({ ...prev, remind_at: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        select
                        label="Repetir"
                        value={reminderForm.recurrence}
                        onChange={e => setReminderForm(prev => ({ ...prev, recurrence: e.target.value }))}
                        SelectProps={{ native: true }}
                        fullWidth
                      >
                        <option value="">No repetir</option>
                        <option value="daily">Todos los días</option>
                        <option value="weekly">Todas las semanas</option>
                      </TextField>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setShowReminderDialog(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={createReminder} disabled={!reminderForm.content || !reminderForm.remind_at}>
                      Crear
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

            {/* Alerts Tab */}
            {currentTab === 6 && (
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <Typography variant="h4" sx={{ mb: 2 }}>Alertas</Typography>
                {alerts.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
                    <Info sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No hay alertas. Cuando el asistente de IA detecte algo importante, te avisará acá.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {alerts.map(alert => (
                      <MuiAlert
                        key={alert.id}
                        severity={alert.severity === 'urgent' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
                        sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="body2" fontWeight={600}>{alert.alert_type}</Typography>
                        <Typography variant="body2">{alert.content}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.created_at).toLocaleString('es-ES')}
                        </Typography>
                      </MuiAlert>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// --- Call History with Summaries (for family) ---
const CallHistoryList = ({ linkedPatients }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryDialog, setSummaryDialog] = useState({ open: false, callId: null, summary: '', loading: false });

  useEffect(() => {
    const fetchHistory = async () => {
      if (!linkedPatients?.length) { setLoading(false); return; }
      try {
        // Fetch call history for each linked patient
        const allCalls = [];
        for (const p of linkedPatients) {
          const data = await apiRequest(`/calls/history?userId=${p.id}`);
          const patientCalls = (data.calls || []).map(c => ({
            ...c,
            patientName: `${p.first_name} ${p.last_name}`,
            companionName: c.employee
              ? `${c.employee.first_name || ''} ${c.employee.last_name || ''}`.trim()
              : null,
            isAICall: !c.employee_id,
          }));
          allCalls.push(...patientCalls);
        }
        // Sort by date desc and only show completed
        allCalls.sort((a, b) => new Date(b.started_at || b.created_at) - new Date(a.started_at || a.created_at));
        setCalls(allCalls.filter(c => c.status === 'completed'));
      } catch (e) {
        setCalls([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [linkedPatients]);

  const fetchSummary = async (callId) => {
    setSummaryDialog({ open: true, callId, summary: '', loading: true });
    try {
      const response = await apiRequest(`/calls/${callId}/notes`);
      const notes = response.notes || [];
      const summaryNote = notes.find(n => n.note_type === 'ai_summary' || n.note_type === 'ai_generated_summary');
      setSummaryDialog({ open: true, callId, summary: summaryNote?.content || 'No hay resumen disponible.', loading: false });
    } catch (e) {
      setSummaryDialog({ open: true, callId, summary: 'Error al cargar el resumen.', loading: false });
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} /></Box>;

  if (calls.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
        <History sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">No hay llamadas en el historial.</Typography>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={1.5}>
        {calls.map(call => (
          <Paper key={call.id} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'white', transition: 'border-color 0.15s', '&:hover': { borderColor: '#CBD5E1' } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: call.isAICall ? '#EEF2FF' : '#F0FDF4', color: call.isAICall ? '#4F46E5' : '#16A34A' }}>
                {call.isAICall ? <SmartToy sx={{ fontSize: 20 }} /> : <VideoCall sx={{ fontSize: 20 }} />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body1" fontWeight={600}>
                    {call.isAICall ? 'Sesión con IA Vincula' : `Sesión con ${call.companionName}`}
                  </Typography>
                  <Chip
                    label={call.isAICall ? 'IA' : 'Humano'}
                    size="small"
                    color={call.isAICall ? 'info' : 'success'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(call.started_at || call.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Duración: {formatDuration(call.duration_seconds || call.duration)}
                  </Typography>
                </Stack>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SummarizeOutlined sx={{ fontSize: 16 }} />}
                onClick={() => fetchSummary(call.id)}
                sx={{ fontSize: '0.8rem' }}
              >
                Ver resumen
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog open={summaryDialog.open} onClose={() => setSummaryDialog({ open: false, callId: null, summary: '', loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Resumen de la llamada</DialogTitle>
        <DialogContent>
          {summaryDialog.loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {summaryDialog.summary}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryDialog({ open: false, callId: null, summary: '', loading: false })}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RecordingsList = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerUrl, setPlayerUrl] = useState('');
  const [playerTitle, setPlayerTitle] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    fetchRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecordings = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/recordings`, {
        headers: {
          'Authorization': `Bearer ${useAuthStore.getState().token}`,
          'X-User-ID': user.id,
          'X-User-Role': user.role,
        },
      });
      const data = await response.json();
      setRecordings(data.recordings || []);
    } catch (error) {
      console.error('Error fetching recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  const playRecording = async (recording) => {
    setLoadingId(recording.id);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/calls/${recording.call_id}/recordings/${recording.id}/url`,
        {
          headers: {
            'Authorization': `Bearer ${useAuthStore.getState().token}`,
            'X-User-ID': user.id,
            'X-User-Role': user.role,
          },
        }
      );
      const data = await response.json();
      if (data.url) {
        setPlayerUrl(data.url);
        setPlayerTitle(`Grabación — ${new Date(recording.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}`);
        setPlayerOpen(true);
      } else {
        alert('No se pudo obtener la URL de la grabación.');
      }
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      alert('Error al obtener la grabación.');
    } finally {
      setLoadingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <Typography variant="body2" color="text.secondary">Cargando grabaciones...</Typography>
      </Box>
    );
  }

  if (recordings.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          py: 8,
          border: '1px dashed',
          borderColor: '#E2E8F0',
          bgcolor: 'white',
        }}
      >
        <VideoLibrary sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
        <Typography variant="h5" color="text.secondary" fontWeight={500} gutterBottom>
          No hay grabaciones disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Las grabaciones aparecerán aquí cuando haya sesiones
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5}>
      {recordings.map((recording) => (
        <Paper
          key={recording.id}
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: '#CBD5E1' },
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Box flex={1}>
              <Typography variant="body1" fontWeight={600} color="text.primary">
                Grabación — {new Date(recording.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
              </Typography>
              <Stack direction="row" spacing={2} mt={0.5}>
                <Typography variant="caption">
                  Duración: {formatDuration(recording.duration_seconds)}
                </Typography>
                <Typography variant="caption">
                  Tamaño: {formatFileSize(recording.recording_size_bytes)}
                </Typography>
              </Stack>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingId === recording.id ? <CircularProgress size={14} /> : <PlayArrow sx={{ fontSize: 16 }} />}
              onClick={() => playRecording(recording)}
              disabled={loadingId === recording.id}
              sx={{ fontSize: '0.8rem' }}
            >
              {loadingId === recording.id ? 'Cargando...' : 'Ver grabación'}
            </Button>
          </Stack>
        </Paper>
      ))}

      {/* Inline Video Player Dialog */}
      <Dialog
        open={playerOpen}
        onClose={() => { setPlayerOpen(false); setPlayerUrl(''); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2 }}>
          <Typography variant="body2" color="grey.300" noWrap sx={{ flex: 1 }}>
            {playerTitle}
          </Typography>
          <IconButton onClick={() => { setPlayerOpen(false); setPlayerUrl(''); }} size="small" sx={{ color: 'grey.400' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
          {playerUrl && (
            <video
              src={playerUrl}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', outline: 'none' }}
              controlsList="nodownload"
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};
